import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  House,
  Files,
  AlertTriangle,
  TrendingUp,
  BarChart2,
  Settings,
  Link as LinkIcon,
  Lightbulb,
  HelpCircle,
  NotebookPen,
  Map,
  Zap,
  Network,
  ShieldCheck,
  ScanSearch,
  Code2,
  ListChecks,
  Bot,
  FileText,
  Users,
  Wrench,
  Quote,
} from "lucide-react";
import { usePlatformAdmin } from "../hooks/usePlatformAdmin";
import { OrganizationSwitcher, UserButton } from "@clerk/clerk-react";
import SiteSwitcher from "./SiteSwitcher";

const PRIMARY = "#018D4C";
const PRIMARY_LIGHT = "#E6F5EE";

// Grouped by workflow: measure → plan content → AI-search coverage → technical → workspace.
const NAV_GROUPS = [
  {
    label: null,
    items: [
      { to: "/dashboard/overview", icon: House, label: "Overview" },
      { to: "/dashboard/tasks", icon: ListChecks, label: "Tasks" },
    ],
  },
  {
    label: "Performance",
    items: [
      { to: "/dashboard/search-performance", icon: BarChart2, label: "Search Performance" },
      { to: "/dashboard/audience", icon: Users, label: "Audience" },
      { to: "/dashboard/reports", icon: FileText, label: "Reports" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/dashboard/articles", icon: Files, label: "Articles" },
      { to: "/dashboard/recommendations", icon: Lightbulb, label: "Recommendations" },
      { to: "/dashboard/writing", icon: NotebookPen, label: "Writing" },
      { to: "/dashboard/cannibalization", icon: AlertTriangle, label: "Cannibalization" },
      { to: "/dashboard/linking", icon: LinkIcon, label: "Internal Linking" },
    ],
  },
  {
    label: "AI Search",
    items: [
      { to: "/dashboard/fanout", icon: Zap, label: "Query Fan-Out" },
      { to: "/dashboard/query-map", icon: Network, label: "Query Map" },
      { to: "/dashboard/query-gaps", icon: ScanSearch, label: "Query Gaps" },
      { to: "/dashboard/ai-citations", icon: Quote, label: "AI Citations" },
      { to: "/dashboard/graph", icon: TrendingUp, label: "Knowledge Graph" },
    ],
  },
  {
    label: "Technical",
    items: [
      { to: "/dashboard/audit", icon: ShieldCheck, label: "SEO Audit" },
      { to: "/dashboard/schema", icon: Code2, label: "Schema Intelligence" },
      { to: "/dashboard/sitemap", icon: Map, label: "Visual Sitemap" },
    ],
  },
  {
    label: "Workspace",
    items: [
      { to: "/dashboard/agents", icon: Bot, label: "Agent Setup" },
      { to: "/dashboard/settings", icon: Settings, label: "Settings" },
    ],
  },
];

// Founder-only entry, appended to Workspace only when /admin/me confirms PLATFORM_ADMIN_USER_IDS.
const ADMIN_NAV = { to: "/dashboard/admin", icon: Wrench, label: "Admin" };

export default function SidebarComponent() {
  const { isAdmin } = usePlatformAdmin();
  const { pathname } = useLocation();
  const groups = isAdmin
    ? NAV_GROUPS.map((g) =>
        g.label === "Workspace" ? { ...g, items: [...g.items, ADMIN_NAV] } : g
      )
    : NAV_GROUPS;
  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-sidebar-border shadow-sm"
    >
      <SidebarHeader className="px-4 py-4 border-b border-sidebar-border group-data-[collapsible=icon]:px-0">
        <div className="flex items-center justify-between group-data-[collapsible=icon]:justify-center">
          <div className="flex items-center gap-2 group-data-[collapsible=icon]:hidden">
            <HelpCircle size={28} style={{ color: PRIMARY }} />
            <span className="text-xl font-bold tracking-tight text-sidebar-foreground" style={{ fontFamily: "Outfit, sans-serif" }}>
              WHY<span style={{ color: PRIMARY }}>RANK</span>
            </span>
          </div>
          <SidebarTrigger className="text-sidebar-foreground/60 hover:bg-transparent hover:text-sidebar-foreground" />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {groups.map(({ label, items }) => (
          <SidebarGroup key={label ?? "home"}>
            {label && (
              <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wide text-sidebar-foreground/50">
                {label}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map(({ to, icon: Icon, label: itemLabel }) => (
                  <SidebarMenuItem key={to}>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname.startsWith(to)}
                      tooltip={itemLabel}
                      className="data-[active=true]:bg-[#018D4C]/20 data-[active=true]:text-[#018D4C] transition-all duration-200"
                    >
                      <NavLink to={to} className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        <span className="text-sm font-medium">{itemLabel}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarSeparator />

      <SidebarFooter className="px-4 py-4 group-data-[collapsible=icon]:px-0">
        <div className="flex flex-col gap-3">
          <div className="group-data-[collapsible=icon]:hidden">
            <SiteSwitcher />
          </div>
          <div className="group-data-[collapsible=icon]:hidden">
            <OrganizationSwitcher
              hidePersonal
              afterCreateOrganizationUrl="/dashboard/overview"
              afterSelectOrganizationUrl="/dashboard/overview"
              appearance={{
                elements: {
                  rootBox: "w-full",
                  organizationSwitcherTrigger:
                    "bg-sidebar hover:bg-sidebar-accent hover:text-sidebar-accent-foreground border border-sidebar-border",
                },
              }}
            />
          </div>
          <div className="flex items-center justify-end border-t border-sidebar-border pt-3 group-data-[collapsible=icon]:justify-center">
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: "w-8 h-8",
                },
              }}
            />
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
